import type { TurnSummary } from "./TurnBreakdown";

/** First line of the Dutch coaching half (before the `---` English split). */
function dutchNote(feedbackMd: string): string {
  const head = feedbackMd.split(/^---\s*$/m)[0]?.trim() ?? "";
  const firstLine = head.split("\n").find((l) => l.trim().length > 0)?.trim() ?? "";
  return firstLine.replace(/^["“]|["”]$/g, "");
}

/** Trim the prompt to a short sub-title (first sentence, capped). */
function shortPrompt(prompt: string): string {
  const firstSentence = prompt.split(/(?<=[.?!])\s/)[0] ?? prompt;
  return firstSentence.length > 80 ? `${firstSentence.slice(0, 79)}…` : firstSentence;
}

/**
 * Compact "Per beurt" recap — one row per turn: a Deel-N chip, the prompt
 * sub-title, and the tutor's Dutch coaching line in serif-italic. The full
 * drill-down (audio, scoring, English) stays in TurnBreakdown below.
 */
export function TurnRecap({ turns, title }: { turns: TurnSummary[]; title: string }) {
  if (turns.length === 0) return null;
  return (
    <section
      aria-label={title}
      className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-5 sm:p-6 shadow-[var(--shadow)] space-y-1"
    >
      <h3 className="text-lg font-semibold tracking-tight mb-3">{title}</h3>
      <ul>
        {turns.map((t, i) => {
          const note = dutchNote(t.feedbackMd);
          return (
            <li
              key={t.turnId}
              className={`flex items-start gap-3.5 py-3.5 ${
                i > 0 ? "border-t border-[var(--border)]" : ""
              }`}
            >
              <span className="shrink-0 rounded-[var(--radius-sm)] bg-[var(--accent-soft)] px-2 py-1 font-display text-[13px] font-semibold text-[var(--accent)]">
                Deel {t.part}
              </span>
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium text-[var(--ink)]">{shortPrompt(t.promptText)}</p>
                {note ? (
                  <p className="font-serif-italic text-sm leading-relaxed text-[var(--ink-muted)]">
                    {note}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
